/* # CommentSection Component
   - Manages and displays comments and attachments for a specific fault report
   - Removed file size displays as requested by user
*/
import { useState, useEffect, useRef } from 'react';
import { LuSend, LuLock, LuPaperclip, LuX, LuFileText, LuImage, LuFileSpreadsheet, LuFile, LuDownload, LuEye, LuTrash2 } from 'react-icons/lu';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { toast } from '../../hooks/use-toast';
import { isManagementTier } from '../../utils/roleCheck';
import { createMentionNotification, createFaultCommentNotification } from '../../utils/notificationHelpers';
import { extractMentionNames, validateMentionedUsers, storeMentions } from '../../utils/mentionUtils';
import MentionsAutocomplete from '../Forum/MentionsAutocomplete';
import MentionText from '../common/MentionText';
import FileViewerModal from './FileViewerModal';

export default function CommentSection({ faultId, isManager: isFaultManager }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [commentAttachments, setCommentAttachments] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const hasManagementAccess = isManagementTier(user?.role);

  useEffect(() => {
    loadComments();
  }, [faultId, user]);

  const loadComments = async () => {
    try {
      const { data, error } = await window.ezsite.apis.tablePage(70651, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'created_at',
        IsAsc: true,
        Filters: [{ name: 'fault_report_id', op: 'Equal', value: Number(faultId) }]
      });
      if (error) throw error;

      const rawComments = data?.List || [];
      const commentsWithAuthors = await Promise.all(
        rawComments.map(async (comment) => {
          try {
            const { data: userData } = await window.ezsite.apis.tablePage(70648, {
              PageNo: 1,
              PageSize: 1,
              Filters: [{ name: 'id', op: 'Equal', value: Number(comment.author_id) }]
            });
            return { ...comment, authorName: userData?.List?.[0]?.name || `User #${comment.author_id}` };
          } catch (e) {
            return { ...comment, authorName: `User #${comment.author_id}` };
          }
        })
      );

      const filteredComments = hasManagementAccess ?
      commentsWithAuthors :
      commentsWithAuthors.filter((c) => !c.is_internal);

      setComments(filteredComments);

      if (filteredComments.length > 0) {
        await loadAttachmentsForComments(filteredComments.map((c) => c.id));
      }
    } catch (error) {
      console.error('[FaultComments] Failed to load:', error);
    }
  };

  const loadAttachmentsForComments = async (commentIds) => {
    if (!commentIds || commentIds.length === 0) return;

    try {
      const uniqueIds = [...new Set(commentIds.map((id) => Number(id)))];

      const attachmentPromises = uniqueIds.map(async (id) => {
        try {
          const { data } = await window.ezsite.apis.tablePage(71362, {
            PageNo: 1,
            PageSize: 100,
            Filters: [{ name: 'comment_id', op: 'Equal', value: id }]
          });
          return data?.List || [];
        } catch (e) {
          console.warn(`[Attachments] Failed to load for comment ${id}:`, e);
          return [];
        }
      });

      const results = await Promise.all(attachmentPromises);
      const attachmentList = results.flat();

      const processedAttachments = await Promise.all(
        attachmentList.map(async (attachment) => {
          try {
            const { data: fileUrl } = await window.ezsite.apis.getUploadUrl(attachment.file_url);
            return {
              ...attachment,
              fileUrl: fileUrl || attachment.file_url
            };
          } catch (e) {
            console.warn(`[Attachments] Failed to get URL for ${attachment.file_name}:`, e);
            return {
              ...attachment,
              fileUrl: attachment.file_url
            };
          }
        })
      );

      const attachmentsByComment = processedAttachments.reduce((acc, attachment) => {
        const commentId = String(attachment.comment_id);
        if (!acc[commentId]) {
          acc[commentId] = [];
        }
        acc[commentId].push(attachment);
        return acc;
      }, {});

      setCommentAttachments(attachmentsByComment);
    } catch (error) {
      console.error('[Attachments] Failed to load:', error);
      setCommentAttachments({});
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const getFileExtension = (filename) => {
    if (!filename) return '';
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
  };

  const getFileInfo = (filename) => {
    const ext = getFileExtension(filename);
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'avif'];
    const spreadsheetExts = ['xls', 'xlsx', 'csv'];
    const pdfExts = ['pdf'];
    const docExts = ['doc', 'docx'];

    if (imageExts.includes(ext)) {
      return { type: 'image', label: 'Image', color: 'text-blue-500', icon: LuImage, bg: 'bg-blue-50' };
    }
    if (spreadsheetExts.includes(ext)) {
      return { type: 'spreadsheet', label: 'Spreadsheet', color: 'text-green-600', icon: LuFileSpreadsheet, bg: 'bg-green-50' };
    }
    if (pdfExts.includes(ext)) {
      return { type: 'pdf', label: 'PDF', color: 'text-red-500', icon: LuFileText, bg: 'bg-red-50' };
    }
    if (docExts.includes(ext)) {
      return { type: 'document', label: 'Document', color: 'text-indigo-500', icon: LuFileText, bg: 'bg-indigo-50' };
    }
    return { type: 'file', label: 'File', color: 'text-gray-500', icon: LuFile, bg: 'bg-gray-50' };
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return [];
    const uploadedFiles = [];
    const totalFiles = selectedFiles.length;
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      try {
        const { data: fileId, error } = await window.ezsite.apis.upload({
          filename: file.name,
          file: file
        });
        if (error) throw error;
        uploadedFiles.push({
          fileId,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type
        });
        setUploadProgress(Math.round((i + 1) / totalFiles * 100));
      } catch (error) {
        console.error(`[Upload] Failed to upload ${file.name}:`, error);
        toast({
          title: 'Upload Error',
          description: `Failed to upload ${file.name}`,
          variant: 'destructive'
        });
      }
    }
    return uploadedFiles;
  };

  const handleMentionProcessing = async (text, commentId, faultTitle) => {
    try {
      const usernames = extractMentionNames(text);
      if (usernames.length === 0) return;
      const validUsers = await validateMentionedUsers(usernames);
      if (validUsers.length === 0) return;
      await storeMentions(validUsers, user.id, 'comment', commentId);
      for (const mentionedUser of validUsers) {
        if (Number(mentionedUser.id) === Number(user.id)) continue;
        await createMentionNotification(
          mentionedUser.id,
          user.name,
          'fault_comment',
          faultId,
          faultTitle || 'a fault report',
          text
        ).catch((e) => console.error('[Mentions] Notification failed:', e));
      }
    } catch (e) {
      console.error('[Mentions] Processing error:', e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() && selectedFiles.length === 0) return;
    setLoading(true);
    setUploadProgress(0);
    try {
      const uploadedFiles = await uploadFiles();
      const { data: faultData } = await window.ezsite.apis.tablePage(70649, {
        PageNo: 1,
        PageSize: 1,
        Filters: [{ name: 'id', op: 'Equal', value: Number(faultId) }]
      });
      const fault = faultData?.List?.[0];
      const { data: commentData, error } = await window.ezsite.apis.tableCreate(70651, {
        fault_report_id: Number(faultId),
        author_id: Number(user.id),
        body: newComment || '(Attachment)',
        is_internal: hasManagementAccess && isInternal,
        created_at: new Date().toISOString()
      });
      if (error) throw error;
      const commentId = commentData?.id || commentData?.ID || commentData;
      if (commentId && uploadedFiles.length > 0) {
        for (const file of uploadedFiles) {
          await window.ezsite.apis.tableCreate(71362, {
            comment_id: Number(commentId),
            file_url: String(file.fileId),
            file_name: file.fileName,
            file_size: file.fileSize,
            file_type: file.fileType,
            uploaded_at: new Date().toISOString(),
            uploaded_by: Number(user.id)
          });
        }
      }
      if (commentId && newComment) {
        await handleMentionProcessing(newComment, commentId, fault?.title);
      }
      if (fault && !isInternal) {
        const reporterId = fault.created_by || fault.reporter_id;
        if (reporterId && Number(reporterId) !== Number(user.id)) {
          await createFaultCommentNotification(
            faultId,
            fault.title,
            reporterId,
            user.name
          ).catch((e) => console.error('[CommentNotify] Failed:', e));
        }
      }
      toast({ title: 'Success', description: 'Comment posted' });
      setNewComment('');
      setIsInternal(false);
      setSelectedFiles([]);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadComments();
    } catch (error) {
      console.error('[CommentSubmit] Error:', error);
      toast({ title: 'Error', description: 'Failed to post comment', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-sm">
      <div className="flex items-center justify-between mb-8">
        <h3 className="font-extrabold text-gray-900 flex items-center gap-3">
          Comments
          <span className="bg-blue-50 text-blue-600 text-[10px] font-bold px-2 py-1 rounded-lg uppercase tracking-wider">
            {comments.length} Total
          </span>
        </h3>
      </div>

      <div className="space-y-6 mb-10">
        {comments.length === 0 ?
        <div className="text-center py-16 bg-gray-50/50 rounded-3xl border border-dashed border-gray-200">
            <p className="text-gray-400 font-bold text-sm uppercase tracking-widest">No conversation yet</p>
          </div> :

        comments.map((comment) => {
          const attachments = commentAttachments[String(comment.id)] || [];
          const isOnlyAttachment = comment.body === '(Attachment)';

          return (
            <div key={comment.id} className={`p-6 rounded-3xl transition-all ${comment.is_internal ? 'bg-amber-50/50 border border-amber-100' : 'bg-gray-50/50 border border-gray-100'}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white text-sm font-bold shadow-md transform -rotate-3">
                      {comment.authorName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-extrabold text-gray-900 text-sm">
                        {String(comment.author_id) === String(user.id) ? 'You' : comment.authorName}
                      </p>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">
                        {comment.created_at ? format(new Date(comment.created_at), 'MMM d, h:mm a') : 'Just now'}
                      </p>
                    </div>
                  </div>
                  {comment.is_internal &&
                <span className="flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-[10px] font-bold text-amber-700 uppercase rounded-xl tracking-tighter">
                      <LuLock className="w-3 h-3" /> Private Note
                    </span>
                }
                </div>
                
                {!isOnlyAttachment &&
              <div className="text-gray-700 text-sm leading-relaxed pl-14">
                    <MentionText text={comment.body} />
                  </div>
              }

                {attachments.length > 0 &&
              <div className={`mt-4 pl-14 space-y-3 ${isOnlyAttachment ? 'pt-2' : ''}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <LuPaperclip className="w-3.5 h-3.5 text-blue-500" />
                      <p className="text-[10px] text-blue-500 font-bold uppercase tracking-widest">
                        {attachments.length} Attachment{attachments.length > 1 ? 's' : ''}
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {attachments.map((attachment) => {
                    const fileInfo = getFileInfo(attachment.file_name);
                    const IconComponent = fileInfo.icon;

                    return (
                      <div
                        key={attachment.id}
                        className="group relative flex flex-col bg-white rounded-2xl border border-gray-200 hover:border-blue-300 transition-all shadow-sm hover:shadow-md overflow-hidden">
                            
                            {/* Image/Icon Area - Fixed Aspect Ratio */}
                            <div
                          className={`relative w-full aspect-[16/9] ${fileInfo.bg} flex items-center justify-center cursor-pointer border-b border-gray-50`}
                          onClick={() => setSelectedFile({
                            url: attachment.fileUrl,
                            fileName: attachment.file_name,
                            type: fileInfo.type
                          })}>

                              {fileInfo.type === 'image' ?
                          <img src={attachment.fileUrl} alt={attachment.file_name} className="w-full h-full object-cover" /> :
                          <IconComponent className={`w-12 h-12 ${fileInfo.color}`} />
                          }
                              
                              {/* Hover Overlay */}
                              <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <LuEye className="w-8 h-8 text-white drop-shadow-lg" />
                              </div>
                            </div>

                            {/* Details Area */}
                            <div className="p-3 flex flex-col gap-2">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-xs font-bold text-gray-900 truncate flex-1" title={attachment.file_name}>
                                  {attachment.file_name}
                                </p>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold uppercase bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded tracking-wide">
                                  {fileInfo.label}
                                </span>
                              </div>

                              {/* Action Buttons Row */}
                              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
                                <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFile({
                                  url: attachment.fileUrl,
                                  fileName: attachment.file_name,
                                  type: fileInfo.type
                                });
                              }}
                              className="flex-1 flex items-center justify-center gap-1.5 p-2 rounded-lg text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors text-[10px] font-bold uppercase tracking-wide"
                              title="Preview">
                                  <LuEye className="w-4 h-4" /> View
                                </button>
                              </div>
                            </div>
                          </div>);
                  })}
                    </div>
                  </div>
              }
              </div>);
        })
        }
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 relative">
        <div className="group">
          <MentionsAutocomplete
            value={newComment}
            onChange={setNewComment}
            placeholder={hasManagementAccess ? "Type @ to mention someone or share an update..." : "Add a comment... Type @ to mention"}
            rows={3} />
        </div>

        {selectedFiles.length > 0 &&
        <div className="space-y-2">
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
              Selected Files ({selectedFiles.length})
            </p>
            <div className="space-y-2">
              {selectedFiles.map((file, index) => {
              const fileInfo = getFileInfo(file.name);
              const IconComponent = fileInfo.icon;
              return (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <div className={`w-10 h-10 ${fileInfo.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <IconComponent className={`w-5 h-5 ${fileInfo.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">{file.name}</p>
                      <p className="text-[10px] text-gray-400 font-bold uppercase">
                        {fileInfo.label}
                      </p>
                    </div>
                    <button
                    type="button"
                    onClick={() => removeSelectedFile(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all">
                      <LuX className="w-4 h-4" />
                    </button>
                  </div>);
            })}
            </div>
          </div>
        }

        {loading && uploadProgress > 0 && uploadProgress < 100 &&
        <div className="space-y-2">
            <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-gray-500">
              <span>Uploading files...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
              className="h-full bg-blue-600 transition-all duration-300"
              style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        }
        
        <div className="flex items-center justify-between pt-2 gap-3">
          <div className="flex items-center gap-3">
            {hasManagementAccess &&
            <label className="flex items-center gap-3 cursor-pointer group">
                <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${isInternal ? 'bg-amber-500 border-amber-500 shadow-lg shadow-amber-100' : 'bg-white border-gray-200 group-hover:border-amber-400'}`}>
                  <input
                  type="checkbox"
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                  className="hidden" />
                  {isInternal && <LuLock className="w-3 h-3 text-white" />}
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-widest ${isInternal ? 'text-amber-600' : 'text-gray-400'}`}>Internal</span>
              </label>
            }

            <label className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all cursor-pointer font-bold text-sm border border-gray-200 hover:border-blue-300">
              <LuPaperclip className="w-4 h-4" />
              <span className="text-[10px] uppercase tracking-widest">Attach</span>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                disabled={loading} />
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || !newComment.trim() && selectedFiles.length === 0}
            className="flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 transition-all font-bold text-sm shadow-xl shadow-blue-100 disabled:opacity-50 active:scale-95">
            <LuSend className="w-4 h-4" /> Post
          </button>
        </div>
      </form>

      {selectedFile &&
      <FileViewerModal
        url={selectedFile.url}
        fileName={selectedFile.fileName}
        type={selectedFile.type}
        onClose={() => setSelectedFile(null)} />
      }
    </div>);
}